import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { ConsultationConnectorAccessType, ConsultationProvider, Module, Onboarding, OnboardingStatus } from '@bciti/types';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { takeUntil, tap } from 'rxjs/operators';

import { BcitiEnvironment } from 'src/app/interfaces/store/store.interface';
import { MatChipInputEvent } from '@angular/material/chips';
import { OnboardingService } from 'src/app/services/onboarding/onboarding.service';
import { State } from 'src/app/store';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { TimeZone } from '@bciti/types';
import { environmentSelector } from 'src/app/store/global/global.selectors';
import { CONFIGURABLE_NOTICE_CHANNELS, NOTICE_CHANNEL_LABELS } from 'src/app/utils/notice-channels';

@Component({
    selector: 'app-onboarding-form',
    templateUrl: './onboarding-form.component.html',
    styleUrls: ['./onboarding-form.component.scss'],
})
export class OnboardingFormComponent implements OnInit, OnDestroy, OnChanges {
    constructor(private fb: FormBuilder, private onboardingService: OnboardingService, private store: Store<State>) {}

    destroy$ = new Subject();

    @Input()
    onboarding: FormGroup;

    @Input()
    loading: boolean = false;

    @Input()
    displayActions: boolean = true;

    @Output()
    submitForm = new EventEmitter<Onboarding>();

    onboardingLoading: boolean = false;
    DEPLOYED = OnboardingStatus.DEPLOYED;
    consultationConnectorAccessType = Object.values(ConsultationConnectorAccessType);
    consultationProvider = Object.values(ConsultationProvider);
    timezones: TimeZone[] = Object.values(TimeZone);

    form = this.fb.group(
        {
            status: this.fb.control(OnboardingStatus.DRAFT),
            organisation: this.fb.group({
                name: this.fb.control('', [Validators.required]),
                tag: this.fb.control('', [Validators.required]),
                logo: this.fb.control(''),
                subdomain: this.fb.control('', [Validators.pattern('^[a-z0-9-]*$')]),
                bcitiId: this.fb.control(0),
                hidden: this.fb.control(true, []),
                entryRoute: this.fb.control(''),
                bounds: this.fb.group({
                    center: this.fb.group({
                        lat: this.fb.control(0),
                        lon: this.fb.control(0),
                    }),
                    northEast: this.fb.group({
                        lat: this.fb.control(0),
                        lon: this.fb.control(0),
                    }),
                    southWest: this.fb.group({
                        lat: this.fb.control(0),
                        lon: this.fb.control(0),
                    }),
                }),
                mapZoom: this.fb.group({
                    min: this.fb.control(10),
                    max: this.fb.control(20),
                    initial: this.fb.control(13),
                }),
                timeZone: this.fb.control(undefined),
                address: this.fb.control(undefined),
            }),
            modules: this.fb.control([]),
            channels: this.fb.control([]),
            core: this.fb.group({
                host: this.fb.control(''),
                port: this.fb.control(443),
                adminHost: this.fb.control(''),
            }),
            platform: this.fb.group({
                // PHASE 2 START
                support: this.fb.control({}),
                primaryCard: this.fb.control({}),
                family: this.fb.control({}),
                adminTabs: this.fb.control({}),
                subTypes: this.fb.control({}),
                // PHASE 2 END
                name: this.fb.control(''),
                lang: this.fb.group({
                    available: this.fb.control([], [Validators.minLength(1)]),
                    default: this.fb.control('', [Validators.required]),
                }),
                ecolocal: this.fb.control(false),
                request: this.fb.group({
                    maxAttachments: this.fb.control(0),
                }),
                dummyAccount: this.fb.control(false),
                emailChannelAutoSubscription: this.fb.control(true),
                cardPrint: this.fb.group({
                    includeCardType: this.fb.control(false),
                    canUserPrintCitizenCard: this.fb.control(true),
                }),
                consultation: this.fb.group({
                    accessType: this.fb.control(undefined),
                    provider: this.fb.control(undefined),
                }),
                frenchLanguageCharterBanner: this.fb.control(false),
                dashboardUrl: this.fb.group({
                    fr: this.fb.control(''),
                    en: this.fb.control(''),
                }),
            }),
            credentials: this.fb.group({
                spreedly: this.fb.group({
                    gateway: this.fb.control(undefined),
                }),
            }),
            feeds: this.fb.group({
                configuration: this.fb.group({
                    classic_organisation_id: this.fb.control(0),
                    defaultNoticeLanguage: this.fb.control(''),
                    defaultNewsLanguage: this.fb.control(''),
                    defaultNewsStatus: this.fb.control(''),
                    endDateDaysGap: this.fb.control(0),
                    newsLanguageUrlInterpretation: this.fb.control(false),
                    newsWordPressFeed: this.fb.control(false),
                    newsCustomFieldsItem: this.fb.control([]),
                }),
                providers: this.fb.control([]),
            }),
            email: this.fb.group({
                header: this.fb.group({
                    villeLogo: this.fb.group({
                        url: this.fb.control(''),
                        height: this.fb.control(0),
                        alt: this.fb.control(''),
                        style: this.fb.control(''),
                    }),
                    bcitiLogo: this.fb.group({
                        url: this.fb.control(''),
                        height: this.fb.control(0),
                        alt: this.fb.control(''),
                        style: this.fb.control(''),
                    }),
                }),
                footer: this.fb.group({
                    block1: this.fb.control(undefined),
                    block2: this.fb.control(undefined),
                    block3: this.fb.control(undefined),
                    block4: this.fb.control(undefined),
                    block5: this.fb.control(undefined),
                }),
            }),
        },
        { updateOn: 'change' },
    );

    newsCustomFieldsItemCtrl = new FormControl();
    uploadingLogo = false;
    modules: Module[] = Object.values(Module);
    channels = CONFIGURABLE_NOTICE_CHANNELS;
    channelLabels = NOTICE_CHANNEL_LABELS;
    matChipSeparatorKeyCodes = [ENTER, COMMA];
    env: Partial<BcitiEnvironment>;
    statusList: OnboardingStatus[] = Object.values(OnboardingStatus);

    get logo() {
        return this.form.get('organisation.logo').value;
    }

    get modulesControl() {
        return this.form.get('modules') as FormGroup;
    }

    get subdomain() {
        return this.form.get('organisation.subdomain').value;
    }

    get subdomainHintLabel() {
        if (!this.subdomain) {
            switch (this.env?.label) {
                case 'DEV':
                    return 'https://dev-vaudreuil.bciti.info';
                case 'UAT':
                    return 'https://uat-vaudreuil.bciti.info';
                case 'PROD':
                    return 'https://vaudreuil.bciti.com';
                default:
                    return 'unknown env';
            }
        } else {
            switch (this.env?.label) {
                case 'DEV':
                    return `https://dev-${this.subdomain}.bciti.info`;
                case 'UAT':
                    return `https://uat-${this.subdomain}.bciti.info`;
                case 'PROD':
                    return `https://${this.subdomain}.bciti.com`;
                default:
                    return 'unknown env';
            }
        }
    }

    get availableLangs() {
        return this.form.get('platform.lang.available').value;
    }

    get newsCustomFieldItems() {
        return this.form.get('feeds.configuration.newsCustomFieldsItem').value;
    }

    get status() {
        return this.form.get('status').value || OnboardingStatus.DRAFT;
    }

    get bcitiId() {
        return this.form.get('organisation.bcitiId').value;
    }

    get classicId() {
        return this.form.get('feeds.configuration.classic_organisation_id').value;
    }

    get consultationControl() {
        return this.form.get('platform.consultation') as FormGroup;
    }

    ngOnInit(): void {
        this.store
            .select(environmentSelector)
            .pipe(tap((env) => (this.env = env)))
            .subscribe();

        this.modulesControl.valueChanges.subscribe((value) => {
            if (!value.includes(Module.CONSULTATION)) {
                this.consultationControl.reset();
            }
        });

        this.consultationControl.valueChanges.subscribe((value) => {
            if (value?.provider === ConsultationProvider.BCITI && !value?.accessType) {
                this.consultationControl.get('accessType').setValue(ConsultationConnectorAccessType.IFRAME);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes?.onboarding?.currentValue) {
            this.form.reset({
                ...this.form.value,
                ...changes.onboarding.currentValue,
            });
            // Set appropriate validators?
        }
        if (changes?.loading?.currentValue) {
            this.form.disable();
        }
        if (!changes?.loading?.currentValue) {
            this.form.reset(this.form.value);
            this.form.enable();
        }
    }

    submit() {
        // -- Phase 1 hack, review in phase2
        this.generateCore();
        this.form.get('platform.name').setValue(this.form.get('organisation.name').value);
        const payload = this.form.value;
        if (payload.organisation.logo === null) {
            payload.organisation.logo = '';
        }
        if (payload.platform.adminTabs === null) {
            payload.platform.adminTabs = {};
        }
        if (payload.platform.primaryCard === null) {
            payload.platform.primaryCard = {};
        }
        if (payload.platform.support === null) {
            payload.platform.support = {};
        }
        if (payload.platform.subTypes === null) {
            payload.platform.subTypes = {};
        }
        if (payload.platform.family === null) {
            payload.platform.family = {};
        }
        if (payload.platform.emailChannelAutoSubscription === null) {
            payload.platform.emailChannelAutoSubscription = true;
        }
        if (payload.platform.frenchLanguageCharterBanner === null) {
            payload.platform.frenchLanguageCharterBanner = false;
        }
        if (payload.organisation.mapZoom.min === null) {
            payload.organisation.mapZoom.min = 13;
        }
        if (payload.organisation.mapZoom.max === null) {
            payload.organisation.mapZoom.max = 20;
        }
        if (payload.organisation.mapZoom.initial === null) {
            payload.organisation.mapZoom.initial = 15;
        }
        if (!payload.organisation.bcitiId) {
            payload.organisation.bcitiId = undefined;
        }

        if (!payload.platform.consultation.accessType && !payload.platform.consultation.provider) {
            payload.platform.consultation = undefined;
        }

        if (payload.platform.cardPrint.canUserPrintCitizenCard === null || payload.platform.cardPrint.canUserPrintCitizenCard === undefined) {
            payload.platform.cardPrint.canUserPrintCitizenCard = true;
        }

        if (!this.availableLangs.includes('fr')) {
            payload.platform.dashboardUrl.fr = null;
        }

        if (!this.availableLangs.includes('en')) {
            payload.platform.dashboardUrl.en = null;
        }

        // --- hack end
        this.submitForm.emit(payload as Onboarding);
    }

    startOnboarding() {
        this.onboardingLoading = true;
        this.loading = true;
        this.onboardingService
            .completeOnbarding(this.form.get('organisation.tag').value)
            .pipe(
                takeUntil(this.destroy$),
                tap((response) => {
                    this.loading = false;
                }),
            )
            .subscribe();
    }

    openInput() {
        document.getElementById('fileInput').click();
    }

    fileChange(files: File[]) {
        if (files.length) {
            this.uploadingLogo = true;
            this.onboardingService
                .uploadLogo(files[0])
                .pipe(
                    takeUntil(this.destroy$),
                    tap((response) =>
                        this.form.patchValue({
                            organisation: { logo: response },
                        }),
                    ),
                    tap(() => (this.uploadingLogo = false)),
                )
                .subscribe();
        } else {
            this.form.patchValue({
                organisation: { logo: undefined },
            });
        }
    }

    removeCustomFieldItem(item): void {
        const items = this.newsCustomFieldItems;
        const index = items.indexOf(item);

        if (index >= 0) {
            items.splice(index, 1);
            this.form.patchValue({
                feeds: { configuration: { newsCustomFieldsItem: items } },
            });
        }
    }

    addCustomFieldItem(event: MatChipInputEvent): void {
        const value = (event.value || '').trim();
        const items = this.newsCustomFieldItems;
        if (value) {
            items.push(value);
        }
        this.form.patchValue({
            feeds: { configuration: { newsCustomFieldsItem: items } },
        });
        this.newsCustomFieldsItemCtrl.setValue(null);
    }

    private generateCore() {
        switch (this.env?.label) {
            case 'DEV':
                if (this.subdomain) {
                    this.form.get('core.host').setValue(`https://dev-${this.subdomain.toLowerCase()}.bciti.info`);
                    this.form.get('core.adminHost').setValue(`https://dev-admin-${this.subdomain.toLowerCase()}.bciti.info`);
                } else {
                    this.form.get('core.host').setValue(`https://dev-lite.bciti.info`);
                    this.form.get('core.adminHost').setValue(`https://dev-lite-admin.bciti.info`);
                }
                break;
            case 'UAT':
                if (this.subdomain) {
                    this.form.get('core.host').setValue(`https://uat-${this.subdomain.toLowerCase()}.bciti.info`);
                    this.form.get('core.adminHost').setValue(`https://uat-admin-${this.subdomain.toLowerCase()}.bciti.info`);
                } else {
                    this.form.get('core.host').setValue(`https://uat-lite.bciti.info`);
                    this.form.get('core.adminHost').setValue(`https://uat-lite-admin.bciti.info`);
                }
                break;
            case 'PROD':
                if (this.subdomain) {
                    this.form.get('core.host').setValue(`https://${this.subdomain.toLowerCase()}.bciti.com`);
                    this.form.get('core.adminHost').setValue(`https://admin-${this.subdomain.toLowerCase()}.bciti.com`);
                } else {
                    this.form.get('core.host').setValue(`https://plus.bciti.com`);
                    this.form.get('core.adminHost').setValue(`https://plus-admin.bciti.com`);
                }
                break;
            default:
                this.form.get('core.host').setValue(``);
                this.form.get('core.adminHost').setValue(``);
                break;
        }
        this.form.get('core.port').setValue(443);
    }
}
